import { safeJSONParse } from 'core/utils';
import QuillLocalStorage from 'core/utils/localStorageClient';
import Router from 'next/router';
import {
  CitationData,
  CitationGeneratorReduxState,
  CitationListAction,
  CitationStylesEnum,
  CitedData,
} from '../types';
import * as actionNames from '../types/names';

const LocalStorage = QuillLocalStorage.getInstance();

const initialState: CitationGeneratorReduxState = {
  citationList: [] as CitationData[],
  citationStyle: CitationStylesEnum.APA,
  citationsFromCowriter: [] as CitationData[],
  citationStyles: [],
  citedDataById: <{ [id: string]: CitedData }>(
    safeJSONParse(LocalStorage.get('citedDataById', '{}'), {})
  ),
};

export default function reducer(
  state: CitationGeneratorReduxState = initialState,
  action: CitationListAction
): CitationGeneratorReduxState {
  switch (action.type) {
    case actionNames.USER_CHANGED_CITATION_STYLE: {
      return {
        ...state,
        citationStyle: action.payload,
      };
    }

    case actionNames.ADD_CITATION_TO_LIST: {
      // use middleware to pass router info in reducer
      let newCitationList = state.citationList;
      let citationListFromPage = 'citationList';
      if (Router && Router.pathname && Router.pathname.includes('/co-writer')) {
        citationListFromPage = 'citationsFromCowriter';
        newCitationList = state.citationsFromCowriter;
      }
      if (
        typeof action.payload?.indexOfCitaionToBeEdit === 'number' &&
        newCitationList[action.payload.indexOfCitaionToBeEdit]
      ) {
        newCitationList[action.payload.indexOfCitaionToBeEdit] = {
          ...action.payload.editFormDefaultValuse,
          id: newCitationList[action.payload.indexOfCitaionToBeEdit].id || null,
          refId: newCitationList[action.payload.indexOfCitaionToBeEdit].refId,
        };
      } else {
        newCitationList = [...newCitationList, { ...action.payload.editFormDefaultValuse }];
      }
      LocalStorage.set(citationListFromPage, JSON.stringify(newCitationList));
      return {
        ...state,
        [citationListFromPage]: [...newCitationList],
      };
    }

    case actionNames.ADD_CITATIONS_TO_LIST: {
      let newCitationList = state.citationList;
      let citationListFromPage = 'citationList';
      if (Router && Router.pathname && Router.pathname.includes('/co-writer')) {
        citationListFromPage = 'citationsFromCowriter';
        newCitationList = state.citationsFromCowriter;
      }
      LocalStorage.set(citationListFromPage, JSON.stringify(action.payload));
      return {
        ...state,
        [citationListFromPage]: [...action.payload],
      };
    }

    case actionNames.REMOVE_CITATION_FROM_LIST: {
      let newCitationList = state.citationList;
      let citationListFromPage = 'citationList';
      if (Router && Router.pathname && Router.pathname.includes('/co-writer')) {
        citationListFromPage = 'citationsFromCowriter';
        newCitationList = state.citationsFromCowriter;
      }
      newCitationList.splice(action.payload, 1);

      LocalStorage.set(citationListFromPage, JSON.stringify(newCitationList));
      return {
        ...state,
        [citationListFromPage]: [...newCitationList], // do not change this; need new object for listner to work properly
      };
    }
    case actionNames.RemoveAllCitaionList: {
      let newCitationList = state.citationList;
      let citationListFromPage = 'citationList';
      if (Router && Router.pathname && Router.pathname.includes('/co-writer')) {
        citationListFromPage = 'citationsFromCowriter';
        newCitationList = state.citationsFromCowriter;
      }
      LocalStorage.set(citationListFromPage, '[]');
      return {
        ...state,
        [citationListFromPage]: [],
      };
    }
    case actionNames.IgnoreWarning: {
      let newCitationList = state.citationList;
      let citationListFromPage = 'citationList';
      if (Router && Router.pathname && Router.pathname.includes('/co-writer')) {
        citationListFromPage = 'citationsFromCowriter';
        newCitationList = state.citationsFromCowriter;
      }
      newCitationList[action.payload].ignore = true;

      return {
        ...state,
        [citationListFromPage]: [...newCitationList],
      };
    }

    case actionNames.SetCitationStyles: {
      // LocalStorage.set('citationStylesJson', JSON.stringify(action.payload));

      return {
        ...state,
        citationStyles: action.payload,
      };
    }

    case actionNames.SetCitedData: {
      const citedDataById: { [id: string]: CitedData } = {};
      action.payload.forEach((cited) => {
        citedDataById[cited.id] = cited;
      });
      LocalStorage.set('citedDataById', JSON.stringify(citedDataById));

      return {
        ...state,
        citedDataById,
      };
    }

    case actionNames.UpdateCitedData: {
      const { citedDataById } = state;
      action.payload.forEach((cited) => {
        citedDataById[cited.id] = cited;
      });
      LocalStorage.set('citedDataById', JSON.stringify(citedDataById));

      return {
        ...state,
        citedDataById,
      };
    }

    case actionNames.AddClonedCitedData: {
      const { citedDataById } = state;
      citedDataById[action.payload.newId] = {
        ...citedDataById[action.payload.oldId],
        id: action.payload.newId,
        citationNumber: action.payload.citationNumber,
      };

      return {
        ...state,
        citedDataById,
      };
    }

    case actionNames.RemoveCitedData: {
      const { citedDataById } = state;
      delete citedDataById[action.payload];
      LocalStorage.set('citedDataById', JSON.stringify(citedDataById));

      return {
        ...state,
        citedDataById,
      };
    }

    case actionNames.RebuildCitationNumbers: {
      let newCitationList = state.citationList;

      if (Router && Router.pathname && Router.pathname.includes('/co-writer')) {
        newCitationList = state.citationsFromCowriter;
      }

      const citedDataById = { ...state.citedDataById };
      newCitationList.forEach((citation, index) => {
        citedDataById[citation.refId].citationNumber = index + 1;
      });

      LocalStorage.set('citedDataById', JSON.stringify(citedDataById));

      return {
        ...state,
        citedDataById,
      };
    }

    default:
      return state;
  }
}
